import {
  Brand,
  Category,
  EnrichedProduct,
  Order,
  ProductInfo,
  ProductVariant,
  QuickReorder,
} from "@/types/AppTypes";
import { parseJSONField } from "@/utils/helper/parseJSON";
import { sortProductByName } from "@/utils/helper/sortProductByName";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { set } from "zod";

interface AppState {
  loading: boolean;
  productInfos: ProductInfo[];
  cartItems: any[];
  cartItemsQuantity: number;
  categories: Category[];
  brands: Brand[];
  productsForDisplay: any[];
  enrichedProductList: EnrichedProduct[];
  brandsLoading: boolean;
  categoriesLoading: boolean;
  productsLoading: boolean;
  quickReorderProducts: QuickReorder[];
  brandsMap?: Record<string, Brand>;
  categoriesMap?: Record<string, Category>;
  orderHistory?: Order[];
}

const getInitialCartItems = (): any[] => {
  if (typeof window !== "undefined") {
    const savedCartItems = localStorage.getItem("cartItems");
    return savedCartItems ? JSON.parse(savedCartItems) : [];
  }
  return [];
};

const calculateCartQuantity = (items: any[]): number => {
  // return items.reduce((total, item) => total + (item.quantity || 0), 0);
  return items.length;
};

const initialCartItems = getInitialCartItems();

const initialState: AppState = {
  loading: false,
  productInfos: [],
  cartItems: initialCartItems,
  cartItemsQuantity: calculateCartQuantity(initialCartItems),
  categories: [],
  brands: [],
  productsForDisplay: [],
  enrichedProductList: [],
  productsLoading: false,
  brandsLoading: false,
  categoriesLoading: false,
  quickReorderProducts: [],
  orderHistory: [],
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setProductInfos: (state, action: PayloadAction<ProductInfo[]>) => {
      state.productInfos = action.payload;
    },
    addToCart: (state, action: PayloadAction<any>) => {
      const product = action.payload;
      const existingProduct = state.cartItems.find(
        (item) =>
          !!item?.product_id &&
          !!product?.product_id &&
          item.product_id === product.product_id &&
          areAttributesEqual(item?.attributes, product?.attributes)
      );
      if (existingProduct) {
        existingProduct.quantity! += product.quantity || 1;
        toast.success(`Updated Quantity`, {
          description: `${product.name}`,
        });
      } else {
        state.cartItems.push({ ...product });
        state.cartItemsQuantity += 1;
        toast.success(`Added to cart`, {
          description: `${product.name}`,
        });
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },
    addToCartBulk: (state, action: PayloadAction<any[]>) => {
      const products = action.payload;
      let newItemsCount = 0;
      let updatedItemsCount = 0;

      products.forEach((product) => {
        const quantityToAdd = product.quantity || 1;
        const existingProduct = state.cartItems.find(
          (item) =>
            !!item?.product_id &&
            !!product?.product_id &&
            item.product_id === product.product_id &&
            areAttributesEqual(item?.attributes, product?.attributes)
        );

        if (existingProduct) {
          existingProduct.quantity! += quantityToAdd;
          updatedItemsCount++;
        } else {
          state.cartItems.push({ ...product, quantity: quantityToAdd });
          newItemsCount++;
        }
      });

      state.cartItemsQuantity = state.cartItems.length;
      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));

      toast.success(`Cart updated`, {
        description: `Added ${newItemsCount} new items, updated ${updatedItemsCount} existing items`,
      });
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      const productId = action.payload;
      const itemToRemove = state.cartItems.find((item) => item?.id === productId);
      if (itemToRemove) {
        state.cartItemsQuantity -= itemToRemove.quantity || 0;
      }
      state.cartItems = state.cartItems.filter((item) => item?.id !== productId);

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },
    clearCart: (state) => {
      state.cartItems = [];
      state.cartItemsQuantity = 0;

      localStorage.setItem("cartItems", JSON.stringify([]));
    },
    updateCartItemQuantity: (state, action: PayloadAction<{ item: any; quantity: number }>) => {
      const { item, quantity } = action.payload;
      const existingProduct = state.cartItems.find(
        (itemm) => itemm?.id === item.id && itemm?.color === item.color
      );
      if (existingProduct) {
        const oldQuantity = existingProduct.quantity || 0;
        state.cartItemsQuantity = state.cartItemsQuantity - oldQuantity + quantity;
        existingProduct.quantity = quantity;
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    setCategories: (state, action: PayloadAction<any[]>) => {
      state.categories = action.payload;
    },
    setBrands: (state, action: PayloadAction<any[]>) => {
      state.brands = action.payload;
    },
    addCategory: (state, action: PayloadAction<any>) => {
      const newCategory = action.payload;
      const index = state.categories.findIndex((category) => category?.id === newCategory?.id);

      if (index !== -1) {
        state.categories[index] = newCategory;
      } else {
        state.categories.push(newCategory);
      }
    },
    addBrand: (state, action: PayloadAction<any>) => {
      const newBrand = action.payload;
      const index = state.brands.findIndex((brand) => brand?.id === newBrand?.id);

      if (index !== -1) {
        state.brands[index] = newBrand;
      } else {
        state.brands.push(newBrand);
      }
    },
    removeCategory: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      state.categories = state.categories.filter((category) => category?.id !== categoryId);
    },
    removeBrand: (state, action: PayloadAction<string>) => {
      const brandId = action.payload;
      state.brands = state.brands.filter((brand) => brand?.id !== brandId);
    },

    setProductsForDisplay: (state, action: PayloadAction<ProductVariant[]>) => {
      state.productsForDisplay = sortProductByName(action.payload);
    },
    setBrandsLoading: (state, action: PayloadAction<boolean>) => {
      state.brandsLoading = action.payload;
    },
    setCategoriesLoading: (state, action: PayloadAction<boolean>) => {
      state.categoriesLoading = action.payload;
    },
    setProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.productsLoading = action.payload;
    },
    setEnrichedProductList: (state, action: PayloadAction<EnrichedProduct[]>) => {
      state.enrichedProductList = action.payload;
    },

    setQuickReorderProducts: (state, action: PayloadAction<QuickReorder[]>) => {
      state.quickReorderProducts = action.payload;
    },
    addQuickReorderProduct: (state, action: PayloadAction<QuickReorder>) => {
      const newProduct = action.payload;
      const existingProductIndex = state.quickReorderProducts.findIndex(
        (product) => product?.id === newProduct?.id
      );

      if (existingProductIndex !== -1) {
        state.quickReorderProducts[existingProductIndex] = newProduct;
      } else {
        state.quickReorderProducts.push(newProduct);
      }
    },
    setBrandsMap: (state, action: PayloadAction<Record<string, Brand>>) => {
      state.brandsMap = action.payload;
    },
    setCategoriesMap: (state, action: PayloadAction<Record<string, Category>>) => {
      state.categoriesMap = action.payload;
    },
    setOrderHistory: (state, action: PayloadAction<Order[]>) => {
      const sortedOrders = [...action.payload].sort(
        (a, b) => new Date(b?.created_at ?? "").getTime() - new Date(a?.created_at ?? "").getTime()
      );
      state.orderHistory = sortedOrders;
    },
    addOrderHistory: (state, action: PayloadAction<Order>) => {
      if (!state.orderHistory) {
        state.orderHistory = [];
      }
      state.orderHistory.push(action.payload);

      state.orderHistory.sort(
        (a, b) => new Date(b?.created_at ?? "").getTime() - new Date(a?.created_at ?? "").getTime()
      );
    },
  },
});

export const {
  setLoading,
  setProductInfos,
  addToCart,
  removeFromCart,
  clearCart,
  updateCartItemQuantity,
  addToCartBulk,
  setCategories,
  setBrands,
  setProductsForDisplay,
  setBrandsLoading,
  setCategoriesLoading,
  setProductsLoading,
  setEnrichedProductList,
  addCategory,
  addBrand,
  removeCategory,
  removeBrand,
  setQuickReorderProducts,
  addQuickReorderProduct,
  setBrandsMap,
  setCategoriesMap,
  setOrderHistory,
  addOrderHistory,
} = appSlice.actions;

export default appSlice.reducer;

export function areAttributesEqual(a?: Record<string, string>, b?: Record<string, string>) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}
